import jsPDF from "jspdf";

/** ---------------- Colors (match StudentEvaluationReport) ---------------- */
const COLORS = {
  primary: { main: [41, 128, 185] },
  text: { dark: [44, 62, 80], light: [236, 240, 241] },
};

/** ---------------- Helpers ---------------- */
const toNumber = (value) => {
  if (value === null || value === undefined || value === "") return 0;
  const n = parseFloat(value);
  return Number.isFinite(n) ? n : 0;
};

const isZeroAmount = (value) => Math.abs(toNumber(value)) < 0.0000001;

const formatCurrency = (value) => {
  const numValue = toNumber(value);
  return new Intl.NumberFormat("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(numValue);
};

/** ---------------- Calculations (Balance) ---------------- */
const firstTermTotalFees = (d) =>
  toNumber(d?.firstTermFee) +
  toNumber(d?.firstTermExamFee) +
  toNumber(d?.firstTermOtherContribution) +
  toNumber(d?.firstTermParentFee);

const firstTermOwed = (d) => firstTermTotalFees(d) + toNumber(d?.firstTermDebt);
const firstTermBalance = (d) => firstTermOwed(d) - toNumber(d?.firstTermPaidAmount);

const secondTermTotalFees = (d) =>
  toNumber(d?.secondTermFee) +
  toNumber(d?.secondTermExamFee) +
  toNumber(d?.secondTermOtherContribution) +
  toNumber(d?.secondTermParentFee);

const secondTermOwed = (d) => secondTermTotalFees(d) + toNumber(d?.secondTermDebt);
const secondTermBalance = (d) => secondTermOwed(d) - toNumber(d?.secondTermPaidAmount);

const hasFirstTerm = (d) =>
  d &&
  (firstTermTotalFees(d) > 0 ||
    toNumber(d.firstTermDebt) > 0 ||
    toNumber(d.firstTermPaidAmount) > 0);

const hasSecondTerm = (d) =>
  d &&
  (secondTermTotalFees(d) > 0 ||
    toNumber(d.secondTermDebt) > 0 ||
    toNumber(d.secondTermPaidAmount) > 0);

/** ---------------- Row builders (filters 0.00) ---------------- */
const buildFirstTermRows = (d) => {
  const rows = [
    { label: "Deni la muhula uliopita", value: d?.firstTermDebt },
    { label: "Ada", value: d?.firstTermFee },
    { label: "Michango ya mitihani", value: d?.firstTermExamFee },
    { label: "Michango mingine", value: d?.firstTermOtherContribution },
    { label: "Michango ya wazazi", value: d?.firstTermParentFee },
    { label: "Kiasi kilicholipwa", value: d?.firstTermPaidAmount },
  ].filter((r) => !isZeroAmount(r.value));

  const bal = firstTermBalance(d);
  if (!isZeroAmount(bal)) rows.push({ label: "Kiasi unachodaiwa", value: bal, emphasize: true });

  return rows;
};

const buildSecondTermRows = (d) => {
  const rows = [
    { label: "Deni la muhula uliopita", value: d?.secondTermDebt },
    { label: "Ada", value: d?.secondTermFee },
    { label: "Michango ya mitihani", value: d?.secondTermExamFee },
    { label: "Michango mingine", value: d?.secondTermOtherContribution },
    { label: "Michango ya wazazi", value: d?.secondTermParentFee },
    { label: "Kiasi kilicholipwa", value: d?.secondTermPaidAmount },
  ].filter((r) => !isZeroAmount(r.value));

  const bal = secondTermBalance(d);
  if (!isZeroAmount(bal)) rows.push({ label: "Kiasi kilichobaki", value: bal, emphasize: true });

  return rows;
};

/** ---------------- PDF Header (style from StudentEvaluationReport) ---------------- */
const drawPdfHeader = (doc, pageWidth) => {
  let currentY = 14;

  doc.setFont("helvetica", "bold");
  doc.setFontSize(16);
  doc.text("ST. MARY'S JUNIOR SEMINARY - VISIGA", pageWidth / 2, currentY, {
    align: "center",
  });

  currentY += 6;
  doc.setFont("helvetica", "normal");
  doc.setFontSize(11);
  doc.text("STUDENT ACCOUNT DETAILS", pageWidth / 2, currentY, {
    align: "center",
  });

  currentY += 6;
  doc.setDrawColor(...COLORS.primary.main);
  doc.setLineWidth(0.3);
  doc.line(10, currentY, pageWidth - 10, currentY);

  currentY += 6;
  return currentY;
};

/** ---------------- PDF Term Box (with header row + middle line divider) ---------------- */
const drawTermBox = (doc, { title, rows, x, y, w }) => {
  const paddingX = 4;
  const rowH = 6;
  const titleBarH = 8;
  const colHeaderH = 7;
  const bottomPad = 4;

  const visibleRowCount = Math.max(rows.length, 1);
  const boxH = titleBarH + colHeaderH + visibleRowCount * rowH + bottomPad;

  // Box
  doc.setDrawColor(0.7, 0.7, 0.7);
  doc.setLineWidth(0.2);
  doc.rect(x, y, w, boxH);

  // Title bar divider
  doc.setDrawColor(0.85, 0.85, 0.85);
  doc.line(x, y + titleBarH, x + w, y + titleBarH);

  // Title
  doc.setFont("helvetica", "bold");
  doc.setFontSize(9);
  doc.text(title, x + paddingX, y + 5.5);

  // Column header separator (line under ITEM/AMOUNT header)
  const headerTop = y + titleBarH;
  const headerBottom = y + titleBarH + colHeaderH;
  doc.setDrawColor(0.85, 0.85, 0.85);
  doc.line(x, headerBottom, x + w, headerBottom);

  // Middle vertical line (separates ITEM vs AMOUNT) — includes headers + values
  const dividerX = x + w * 0.73; // 73% label | 27% amount
  doc.setDrawColor(0.85, 0.85, 0.85);
  doc.line(dividerX, headerTop, dividerX, y + boxH);

  // Column header labels
  doc.setFont("helvetica", "bold");
  doc.setFontSize(8);
  doc.text("ITEM", x + paddingX, y + titleBarH + 5);
  doc.text("AMOUNT", x + w - paddingX, y + titleBarH + 5, { align: "right" });

  let yy = y + titleBarH + colHeaderH + 5;

  if (!rows || rows.length === 0) {
    doc.setFont("helvetica", "italic");
    doc.setFontSize(8);
    doc.text("No charges recorded", x + paddingX, yy);
    return y + boxH;
  }

  const valueX = x + w - paddingX;

  rows.forEach((r) => {
    doc.setFont("helvetica", r.emphasize ? "bold" : "normal");
    doc.setFontSize(8);
    doc.text(String(r.label), x + paddingX, yy, {
      maxWidth: dividerX - (x + paddingX) - 2,
    });
    doc.text(formatCurrency(r.value), valueX, yy, { align: "right" });
    yy += rowH;
  });

  return y + boxH;
};

/** ---------------- PDF: Financial Details (two columns if both terms exist) ---------------- */
const drawFinancialDetails = (doc, accountantData, margin, pageWidth, yStart) => {
  let y = yStart;

  doc.setFont("helvetica", "bold");
  doc.setFontSize(11);
  doc.text("TAARIFA ZA KIFEDHA", margin, y);
  y += 8;

  const showFirst = hasFirstTerm(accountantData);
  const showSecond = hasSecondTerm(accountantData);

  // Even if no term has data, show one box with empty message (neat)
  if (!showFirst && !showSecond) {
    const w = pageWidth - 2 * margin;
    y = drawTermBox(doc, { title: "Term Details", rows: [], x: margin, y, w }) + 6;
    return y;
  }

  const firstRows = showFirst ? buildFirstTermRows(accountantData) : [];
  const secondRows = showSecond ? buildSecondTermRows(accountantData) : [];

  const gap = 6;

  if (showFirst && showSecond) {
    const w = (pageWidth - 2 * margin - gap) / 2;
    const leftX = margin;
    const rightX = margin + w + gap;

    const endLeft = drawTermBox(doc, { title: "First Term", rows: firstRows, x: leftX, y, w });
    const endRight = drawTermBox(doc, { title: "Second Term", rows: secondRows, x: rightX, y, w });

    y = Math.max(endLeft, endRight) + 6;
  } else {
    const w = pageWidth - 2 * margin;
    const title = showFirst ? "First Term" : "Second Term";
    const rows = showFirst ? firstRows : secondRows;
    y = drawTermBox(doc, { title, rows, x: margin, y, w }) + 6;
  }

  return y;
};

/**
 * Build the account details PDF (shared by save and print).
 * @returns {{ doc, fileName }}
 */
const buildAccountDetailsPdf = (accountantData, studentData) => {
  const doc = new jsPDF("p", "mm", "a4");
  const pageWidth = doc.internal.pageSize.getWidth();
  const margin = 12;

  let y = drawPdfHeader(doc, pageWidth);

  const studentName = studentData?.studentName || "N/A";
  const className = studentData?.className || "N/A";
  const studentNumber = studentData?.studentNumber || "N/A";
  const termName = accountantData?.termName || "N/A";

  doc.setFont("helvetica", "bold");
  doc.setFontSize(11);
  doc.text("TAARIFA ZA MSEMINARI", margin, y);
  y += 6;

  const labelX = margin;
  const valueX = 55;
  doc.setFontSize(9);

  const info = [
    ["Jina la Mseminari:", studentName],
    ["Darasa:", className],
    ["Namba ya Index:", studentNumber],
    ["Muhula:", termName],
  ];
  info.forEach(([label, value]) => {
    doc.setFont("helvetica", "bold");
    doc.text(label, labelX, y);
    doc.setFont("helvetica", "normal");
    doc.text(value, valueX, y);
    y += 4.5;
  });
  y += 2;

  y = drawFinancialDetails(doc, accountantData, margin, pageWidth, y);

  doc.setDrawColor(...COLORS.primary.main);
  doc.setLineWidth(0.3);
  doc.line(margin, y, pageWidth - margin, y);
  y += 8;

  doc.setFont("helvetica", "bold");
  doc.setFontSize(11);
  doc.text("Malipo yafanyike kwenye Account namba zifuatazo:-", margin, y);
  y += 7;

  doc.setFont("helvetica", "normal");
  doc.setFontSize(9);
  doc.text("CRDB BANK: 0152253103800", margin, y);
  y += 5;
  doc.text("NMB BANK: 21210019782", margin, y);
  y += 5;
  doc.text("MKOMBOZI BANK: 00120600737701", margin, y);
  y += 7;

  doc.setFont("helvetica", "bold");
  doc.text("ACCOUNT NAME:", margin, y);
  y += 5;
  doc.setFont("helvetica", "normal");
  doc.text("ST. MARY'S JUNIOR SEMINARY – VISIGA", margin, y);
  y += 5;
  doc.setFont("helvetica", "bold");
  doc.text("Namba ya simu ya Seminari:", margin, y);
  y += 5;
  doc.setFont("helvetica", "normal");
  doc.text("+255 767 927 537 (St. Mary's Seminary)", margin, y);

  const fileName = `Account_${(studentName || "Student").replace(/\s+/g, "_")}_${String(
    termName
  ).replace(/\s+/g, "_")}.pdf`;
  return { doc, fileName };
};

/**
 * Generate and download a one-page PDF with student account details and payment instructions.
 */
export const generateStudentAccountDetailsPdf = (accountantData, studentData) => {
  const { doc, fileName } = buildAccountDetailsPdf(accountantData, studentData);
  doc.save(fileName);
};

/** ---------------- Build HTML for print (hide 0.00 rows + new header + column-wise student info) ---------------- */
const getPrintHtml = (accountantData, studentData) => {
  const studentName = studentData?.studentName || "N/A";
  const className = studentData?.className || "N/A";
  const studentNumber = studentData?.studentNumber || "N/A";
  const termName = accountantData?.termName || "N/A";

  const showFirst = hasFirstTerm(accountantData);
  const showSecond = hasSecondTerm(accountantData);

  const firstRows = showFirst ? buildFirstTermRows(accountantData) : [];
  const secondRows = showSecond ? buildSecondTermRows(accountantData) : [];

  const termTable = (title, rows) => {
    if (!rows || rows.length === 0) {
      return `
        <div class="term">
          <div class="term-title">${title}</div>
          <div class="empty">No charges recorded</div>
        </div>
      `;
    }

    const trs = rows
      .map(
        (r) => `
          <tr class="${r.emphasize ? "em" : ""}">
            <td class="label">${r.label}</td>
            <td class="value">${formatCurrency(r.value)}</td>
          </tr>`
      )
      .join("");

    return `
      <div class="term">
        <div class="term-title">${title}</div>
        <table class="financial">
          <thead>
            <tr>
              <th class="thLabel">ITEM</th>
              <th class="thValue">AMOUNT</th>
            </tr>
          </thead>
          <tbody>${trs}</tbody>
        </table>
      </div>
    `;
  };

  const layoutClass = showFirst && showSecond ? "two" : "one";

  // If no term data, show one empty box
  const termBoxes =
    !showFirst && !showSecond
      ? termTable("Term Details", [])
      : `${showFirst ? termTable("First Term", firstRows) : ""}${
          showSecond ? termTable("Second Term", secondRows) : ""
        }`;

  return `
<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8">
  
  <style>
    * { box-sizing: border-box; }
    body { font-family: Helvetica, Arial, sans-serif; font-size: 11px; padding: 14px; margin: 0; max-width: 210mm; color:#111; }

    /* Header style (match StudentEvaluationReport) */
    .docHeader { text-align: center; margin-bottom: 6px; }
    .docHeader .school { font-weight: 800; font-size: 16px; letter-spacing: .2px; }
    .docHeader .sub { margin-top: 6px; font-size: 11px; font-weight: normal; }
    .docHeader .divider { border-top: 2px solid rgb(41, 128, 185); margin: 6px 0 0 0; }

    /* Student info (column-wise) */
    .info { margin: 10px 0 12px 0; border: 1px solid #e6e6e6; border-radius: 10px; overflow: hidden; }
    .info-row { display: grid; grid-template-columns: 110px 1fr; padding: 8px 10px; border-bottom: 1px solid #f0f0f0; }
    .info-row:last-child { border-bottom: none; }
    .info-row .k { font-weight: 700; }
    .info-row .v { }

    .section-title { font-weight: 800; font-size: 12px; margin: 12px 0 8px 0; }

    /* Terms layout */
    .terms.one { display: block; }
    .terms.two { display: flex; gap: 10px; }

    .term { border: 1px solid #cfcfcf; border-radius: 10px; overflow: hidden; flex: 1; }
    .term-title { background: #f5f5f5; padding: 8px 10px; font-weight: 800; border-bottom: 1px solid #e0e0e0; }

    /* Table with middle line separator */
    table.financial { width: 100%; border-collapse: collapse; font-size: 10px; }
    table.financial th, table.financial td { padding: 7px 10px; border-bottom: 1px solid #eee; }
    table.financial thead th { background: #fafafa; font-weight: 800; border-bottom: 1px solid #e0e0e0; }

    .thLabel, td.label { border-right: 1px solid #e0e0e0; width: 70%; text-align: left; }
    .thValue, td.value { width: 30%; text-align: right; font-weight: 800; }

    table.financial tr:last-child td { border-bottom: none; }
    tr.em td { font-weight: 900; background: #fafafa; }

    .empty { padding: 10px; font-style: italic; color: #555; }

    .line { border-top: 1px solid #000; margin: 12px 0; }

    @media print { body { -webkit-print-color-adjust: exact; print-color-adjust: exact; } }
    @page { size: A4; margin: 14mm; }
  </style>
</head>
<body>  <div class="docHeader">
    <div class="school">ST. MARY'S JUNIOR SEMINARY - VISIGA</div>
    <div class="sub">MICHANGO NA ADA YA MUHULA UNAOFUATA WA MASOMO</div>
    <div class="divider"></div>
  </div>

  <div class="info">
    <div class="info-row"><div class="k">Jina la mseminari</div><div class="v">${studentName}</div></div>
    <div class="info-row"><div class="k">Darasa</div><div class="v">${className}</div></div>
    <div class="info-row"><div class="k">Number ya mtihani</div><div class="v">${studentNumber}</div></div>
    <div class="info-row"><div class="k">Muhula</div><div class="v">${termName}</div></div>
  </div>

  <div class="section-title">TAARIFA ZA KIFEDHA</div>

  <div class="terms ${layoutClass}">
    ${termBoxes}
  </div>

  <div class="line"></div>

  <p style="margin:8px 0 6px 0;font-weight:800">Malipo yafanyike kwenye Account namba zifuatazo:-</p>
  <p style="margin:2px 0">CRDB BANK: 0152253103800</p>
  <p style="margin:2px 0">NMB BANK: 21210019782</p>
  <p style="margin:2px 0">MKOMBOZI BANK: 00120600737701</p>

  <p style="margin:10px 0 2px 0;font-weight:800">ACCOUNT NAME</p>
  <p style="margin:0 0 6px 0">ST. MARY'S JUNIOR SEMINARY – VISIGA</p>
  <p style="margin:0 0 2px 0;font-weight:800">Namba ya simu ya Seminari</p>
  <p style="margin:0">+255 767 927 537 (St. Mary's Seminary)</p>

</body>
</html>`;
};

/**
 * Open the PDF in a new tab and trigger print. Printing the PDF (not HTML) avoids
 * "about:blank" in the tab and avoids the browser adding date/time on the printed page.
 */
export const printStudentAccountDetails = (accountantData, studentData) => {
  const { doc } = buildAccountDetailsPdf(accountantData, studentData);
  const blob = doc.output("blob");
  const url = URL.createObjectURL(blob);
  const printWindow = window.open(url, "_blank");
  if (!printWindow) {
    URL.revokeObjectURL(url);
    return;
  }
  printWindow.onload = () => {
    printWindow.focus();
    printWindow.print();
  };
  printWindow.onafterprint = () => {
    URL.revokeObjectURL(url);
    printWindow.close();
  };
};